import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    IconButton,
    TableRow,
    TableCell,
    tableCellClasses,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Switch,
    FormControlLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { showLoader } from '../../Redux/Slices/loaderSlice';
import ErrorHandler from '../../Utils/handler';
import { toast } from 'react-toastify';
import CommonTable from '../../Components/Common/CommonTable';
import DataNotFound from '../../Components/Common/DataNotFound';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { BaseURL } from '../../ApiSetup/Api';
import sessionStoreUtil from '../../ApiSetup/Localstorage';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        color: theme?.palette?.bgLightWhite?.main,
        fontSize: 14,
        textAlign: 'center',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        textAlign: 'center',
        padding: '12px 8px',
    },
}));

const defaultFormData = {
    name: '',
    nameGuj: '',
    description: '',
    icon: '🎨',
    color: '#3E4094',
    accessLevel: 'registered',
    platforms: { app: true, website: true },
    autoGenerateEvent: 'none',
    autoGenerateEnabled: false,
    sortOrder: 0,
    isActive: true,
};

const accessLevelOptions = [
    { value: 'all', label: 'All Users (Including Guests)' },
    { value: 'registered', label: 'Registered Users Only' },
    { value: 'app_only', label: 'App Users Only' },
    { value: 'admin_only', label: 'Admin Only' },
];

const autoGenerateOptions = [
    { value: 'none', label: 'None' },
    { value: 'birthday', label: 'Birthday' },
    { value: 'anniversary', label: 'Anniversary' },
    { value: 'registration', label: 'Registration' },
    { value: 'festival', label: 'Festival' },
];

const PostCategories = () => {
    const dispatch = useDispatch();

    const [categories, setCategories] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [formData, setFormData] = useState(defaultFormData);
    const [errors, setErrors] = useState({});

    const headerList = ['No', 'Icon', 'Name', 'Name (Gujarati)', 'Access', 'Platform', 'Auto-Generate', 'Templates', 'Status', 'Actions'];

    const getAuthHeaders = () => {
        const token = localStorage.getItem("auth_token");
        return { Authorization: token };
    };

    const fetchCategories = async () => {
        dispatch(showLoader(true));
        try {
            const response = await axios.post(
                `${BaseURL}admin/post-generator/category/get`,
                { includeInactive: true },
                { headers: getAuthHeaders() }
            );
            if (response?.data?.success) {
                setCategories(response?.data?.data || []);
            }
        } catch (error) {
            console.error('Fetch categories error:', error);
            toast.error('Failed to fetch categories');
        } finally {
            dispatch(showLoader(false));
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    const handleOpenDialog = (category = null) => {
        if (category) {
            setSelectedCategory(category);
            setFormData({
                name: category.name || '',
                nameGuj: category.nameGuj || '',
                description: category.description || '',
                icon: category.icon || '🎨',
                color: category.color || '#3E4094',
                accessLevel: category.accessLevel || 'registered',
                platforms: category.platforms || { app: true, website: true },
                autoGenerateEvent: category.autoGenerateEvent || 'none',
                autoGenerateEnabled: category.autoGenerateEnabled || false,
                sortOrder: category.sortOrder || 0,
                isActive: category.isActive !== false,
            });
        } else {
            setSelectedCategory(null);
            setFormData(defaultFormData);
        }
        setErrors({});
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedCategory(null);
        setFormData(defaultFormData);
    };

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };

    const handlePlatformChange = (platform, value) => {
        setFormData((prev) => ({
            ...prev,
            platforms: { ...prev.platforms, [platform]: value }
        }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        dispatch(showLoader(true));
        try {
            const payload = { ...formData };

            if (selectedCategory) {
                payload.id = selectedCategory._id;
                await axios.put(
                    `${BaseURL}admin/post-generator/category/update`,
                    payload,
                    { headers: getAuthHeaders() }
                );
                toast.success('Category updated successfully');
            } else {
                await axios.post(
                    `${BaseURL}admin/post-generator/category/create`,
                    payload,
                    { headers: getAuthHeaders() }
                );
                toast.success('Category created successfully');
            }

            handleCloseDialog();
            fetchCategories();
        } catch (error) {
            console.error('Submit error:', error);
            toast.error(error?.response?.data?.message || 'Failed to save category');
        } finally {
            dispatch(showLoader(false));
        }
    };

    const handleDelete = async () => {
        if (!selectedCategory) return;

        dispatch(showLoader(true));
        try {
            await axios.delete(
                `${BaseURL}admin/post-generator/category/${selectedCategory._id}`,
                { headers: getAuthHeaders() }
            );
            toast.success('Category deleted successfully');
            setDeleteDialogOpen(false);
            setSelectedCategory(null);
            fetchCategories();
        } catch (error) {
            console.error('Delete error:', error);
            toast.error(error?.response?.data?.message || 'Failed to delete category');
        } finally {
            dispatch(showLoader(false));
        }
    };

    const openDeleteDialog = (category) => {
        setSelectedCategory(category);
        setDeleteDialogOpen(true);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" fontWeight="bold">
                        📂 Post Categories
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        Add Category
                    </Button>
                </Box>

                {categories.length > 0 ? (
                    <CommonTable headerList={headerList}>
                        {categories.map((category, index) => (
                            <TableRow key={category._id}>
                                <StyledTableCell>{index + 1}</StyledTableCell>
                                <StyledTableCell>
                                    <span style={{ fontSize: '24px' }}>{category.icon}</span>
                                </StyledTableCell>
                                <StyledTableCell>{category.name}</StyledTableCell>
                                <StyledTableCell>{category.nameGuj || '-'}</StyledTableCell>
                                <StyledTableCell>
                                    <Chip
                                        label={accessLevelOptions.find(o => o.value === category.accessLevel)?.label || category.accessLevel}
                                        size="small"
                                        color={category.accessLevel === 'all' ? 'success' : 'primary'}
                                    />
                                </StyledTableCell>
                                <StyledTableCell>
                                    {category.platforms?.app && <Chip label="App" size="small" sx={{ mr: 0.5 }} />}
                                    {category.platforms?.website && <Chip label="Web" size="small" />}
                                </StyledTableCell>
                                <StyledTableCell>
                                    {category.autoGenerateEvent !== 'none' ? (
                                        <Chip
                                            label={category.autoGenerateEvent}
                                            size="small"
                                            color={category.autoGenerateEnabled ? 'success' : 'default'}
                                        />
                                    ) : '-'}
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Chip label={category.templateCount || 0} size="small" />
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Chip
                                        label={category.isActive ? 'Active' : 'Inactive'}
                                        size="small"
                                        color={category.isActive ? 'success' : 'default'}
                                    />
                                </StyledTableCell>
                                <StyledTableCell>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleOpenDialog(category)}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => openDeleteDialog(category)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </StyledTableCell>
                            </TableRow>
                        ))}
                    </CommonTable>
                ) : (
                    <DataNotFound title="No categories found" />
                )}
            </Paper>

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {selectedCategory ? 'Edit Category' : 'Add New Category'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Category Name"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                error={!!errors.name}
                                helperText={errors.name}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Name (Gujarati)"
                                value={formData.nameGuj}
                                onChange={(e) => handleChange('nameGuj', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                multiline
                                rows={2}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                fullWidth
                                label="Icon"
                                value={formData.icon}
                                onChange={(e) => handleChange('icon', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                fullWidth
                                label="Color"
                                type="color"
                                value={formData.color}
                                onChange={(e) => handleChange('color', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Sort Order"
                                type="number"
                                value={formData.sortOrder}
                                onChange={(e) => handleChange('sortOrder', parseInt(e.target.value) || 0)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Access Level</InputLabel>
                                <Select
                                    value={formData.accessLevel}
                                    label="Access Level"
                                    onChange={(e) => handleChange('accessLevel', e.target.value)}
                                >
                                    {accessLevelOptions.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>Platforms</Typography>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.platforms.app}
                                        onChange={(e) => handlePlatformChange('app', e.target.checked)}
                                    />
                                }
                                label="Mobile App"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.platforms.website}
                                        onChange={(e) => handlePlatformChange('website', e.target.checked)}
                                    />
                                }
                                label="Website"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Auto-Generate Event</InputLabel>
                                <Select
                                    value={formData.autoGenerateEvent}
                                    label="Auto-Generate Event"
                                    onChange={(e) => handleChange('autoGenerateEvent', e.target.value)}
                                >
                                    {autoGenerateOptions.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.autoGenerateEnabled}
                                        onChange={(e) => handleChange('autoGenerateEnabled', e.target.checked)}
                                        disabled={formData.autoGenerateEvent === 'none'}
                                    />
                                }
                                label="Enable Auto-Generation"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={(e) => handleChange('isActive', e.target.checked)}
                                    />
                                }
                                label="Active"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit}>
                        {selectedCategory ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Category</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "{selectedCategory?.name}"?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PostCategories;
